/**
 * Automotive Premium — Animation & Effects Layer
 * Shopify Liquid theme enhancement (no framework dependency)
 * Runs after DOM is ready; progressively enhances existing markup.
 */

(function () {
  'use strict';

  /* ── 1. CURSOR GLOW ─────────────────────────────────────────── */
  function initCursorGlow() {
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const glow = document.createElement('div');
    glow.id = 'ap-cursor-glow';
    document.body.appendChild(glow);

    let mouseX = 0, mouseY = 0;
    let glowX = 0, glowY = 0;
    let rafId = null;

    document.addEventListener('mousemove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!rafId) rafId = requestAnimationFrame(animateGlow);
    }, { passive: true });

    function animateGlow() {
      glowX += (mouseX - glowX) * 0.08;
      glowY += (mouseY - glowY) * 0.08;
      glow.style.left = glowX + 'px';
      glow.style.top  = glowY + 'px';
      rafId = requestAnimationFrame(animateGlow);
    }
  }

  /* ── 2. SCROLL REVEAL (IntersectionObserver) ─────────────────── */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;

    const targets = [
      '.product-card',
      '.collection-card',
      '.featured-blog-posts-card',
      '.hero__content-wrapper > *',
      '.section-heading',
      '.review',
      '.media-with-content',
      'footer .footer-content > *',
    ];

    const allElements = document.querySelectorAll(targets.join(', '));

    allElements.forEach(function (el) {
      el.classList.add('ap-fade-in');
    });

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ap-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px',
    });

    allElements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── 3. PRODUCT GRID STAGGER ──────────────────────────────────── */
  function initProductGridStagger() {
    if (!('IntersectionObserver' in window)) return;

    const grids = document.querySelectorAll('.product-grid, ul[class*="product-grid"]');
    grids.forEach(function (grid) {
      grid.classList.add('ap-stagger');
    });

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ap-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -60px 0px' });

    grids.forEach(function (grid) { observer.observe(grid); });
  }

  /* ── 4. HERO PARALLAX (subtle scroll) ────────────────────────── */
  function initHeroParallax() {
    const hero = document.querySelector('.hero__media-grid');
    if (!hero) return;

    let lastScroll = 0;
    let ticking = false;

    window.addEventListener('scroll', function () {
      lastScroll = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(function () {
          const offset = lastScroll * 0.25;
          hero.style.transform = 'translateY(' + offset + 'px)';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── 5. HEADER SCROLL STATE ───────────────────────────────────── */
  function initHeaderScroll() {
    const header = document.querySelector('.header, header-component');
    if (!header) return;

    let lastY = 0;
    let ticking = false;

    window.addEventListener('scroll', function () {
      const y = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(function () {
          if (y > 80) {
            header.setAttribute('data-ap-scrolled', 'true');
          } else {
            header.removeAttribute('data-ap-scrolled');
          }
          lastY = y;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── 6. PRODUCT IMAGE HOVER 3D TILT ──────────────────────────── */
  function initCardTilt() {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cards = document.querySelectorAll('.product-card');

    cards.forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width  - 0.5;
        const y = (e.clientY - rect.top)  / rect.height - 0.5;
        const rotX = -(y * 5);
        const rotY =  (x * 5);
        card.style.transform =
          'perspective(800px) rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg) translateY(-6px) scale(1.015)';
      }, { passive: true });

      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ── 7. SMOOTH CART DRAWER TRANSITION ────────────────────────── */
  function initCartDrawerTransition() {
    const drawer = document.querySelector('cart-drawer, #cart-drawer, .cart-drawer');
    if (!drawer) return;

    const obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'open' || mutation.attributeName === 'class') {
          const isOpen = drawer.hasAttribute('open') ||
                         drawer.classList.contains('is-open') ||
                         drawer.classList.contains('active');
          drawer.style.transition = 'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.35s ease';
          if (isOpen) {
            drawer.style.transform = 'translateX(0)';
            drawer.style.opacity = '1';
          }
        }
      });
    });

    obs.observe(drawer, { attributes: true });
  }

  /* ── 8. SECTION DIVIDER GLOW INJECTION ───────────────────────── */
  function injectSectionDividers() {
    const sections = document.querySelectorAll('.shopify-section:not(.header-section):not(:last-child)');
    sections.forEach(function (section) {
      const divider = document.createElement('div');
      divider.className = 'section-divider-glow';
      section.after(divider);
    });
  }

  /* ── 9. ANNOUNCEMENT BAR — SUBTLE SHIMMER ────────────────────── */
  function initAnnouncementShimmer() {
    const bar = document.querySelector('.announcement-bar, .header-announcements');
    if (!bar) return;

    bar.style.position = 'relative';
    bar.style.overflow = 'hidden';

    const shimmer = document.createElement('span');
    Object.assign(shimmer.style, {
      position: 'absolute',
      top: '0',
      left: '-100%',
      width: '50%',
      height: '100%',
      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)',
      pointerEvents: 'none',
      animation: 'ap-shimmer 4s ease infinite',
    });

    if (!document.getElementById('ap-shimmer-keyframes')) {
      const style = document.createElement('style');
      style.id = 'ap-shimmer-keyframes';
      style.textContent = '@keyframes ap-shimmer { 0%{left:-100%} 60%{left:150%} 100%{left:150%} }';
      document.head.appendChild(style);
    }

    bar.appendChild(shimmer);
  }

  /* ── 10. IMAGE LAZY LOAD FADE ────────────────────────────────── */
  function initImageFade() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    images.forEach(function (img) {
      if (img.closest('media-gallery, drag-zoom-wrapper, .media-gallery, .product__media-gallery, .product__media')) return;
      if (img.complete) return;
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.5s ease';
      function restore() { img.style.opacity = '1'; }
      img.addEventListener('load',  restore, { once: true });
      img.addEventListener('error', restore, { once: true });
      setTimeout(restore, 2500);
    });
  }

  /* ── 11. QUICK-ADD BUTTON REVEAL ON HOVER ─────────────────────── */
  function initQuickAddReveal() {
    const cards = document.querySelectorAll('.product-card');
    cards.forEach(function (card) {
      const quickAdd = card.querySelector('.quick-add, .quick-add-button, [class*="quick-add"]');
      if (!quickAdd) return;

      quickAdd.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      quickAdd.style.opacity = '0';
      quickAdd.style.transform = 'translateY(6px)';

      card.addEventListener('mouseenter', function () {
        quickAdd.style.opacity = '1';
        quickAdd.style.transform = 'translateY(0)';
      });
      card.addEventListener('mouseleave', function () {
        quickAdd.style.opacity = '0';
        quickAdd.style.transform = 'translateY(6px)';
      });
    });
  }

  /* ── 12. INIT ──────────────────────────────────────────────────── */
  function init() {
    initCursorGlow();
    initScrollReveal();
    initProductGridStagger();
    initHeroParallax();
    initHeaderScroll();
    initCardTilt();
    initCartDrawerTransition();
    injectSectionDividers();
    initAnnouncementShimmer();
    initImageFade();
    initQuickAddReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run card effects after Shopify section re-renders (design mode) */
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initCardTilt();
    initQuickAddReveal();
  });

})();
